import React, { useState, FunctionComponent, SyntheticEvent } from 'react';
import { Form } from 'react-bootstrap';

import { PrimaryButton, SecondaryButton } from '@components/library/button';
import Tooltip from '@components/common/tooltip/main';
import { submitNewCustomFields } from '@api/v4/resources/custom_fields';
import {
  deleteCustomFieldKey,
  updateCustomFieldKeys,
} from '@api/v4/custom-field-keys';
import { ApiDatumResponse } from '@api/v4/ApiResponseTypes';
import { CustomFieldKeyClient, CustomFieldKeyServer, CustomFieldKeysResponseObject } from '@api/v4/resources/CustomFieldKeysTypes';
import { determineUGTLocale } from '@components/asset/modal/tabs/edit/helpers';
import { DisplayedCustomFieldKey } from '@components/brandfolder/settings/custom-fields-tab/CustomFieldsTab';
import { CustomFieldRow } from './CustomFieldRow';

interface CustomFieldsFormProps {
  brandfolderKey: string;
  fetchCustomFieldKeys: () => Promise<void>;
  hasRolloutMultiValueCustomFieldsFeature: boolean;
  rows: DisplayedCustomFieldKey[];
  setRows: SetStateDispatch<DisplayedCustomFieldKey[]>;
}

export interface UpdateRowArguments extends Partial<CustomFieldKeyClient> {
  index?: number;
}

const blankCustomFieldKeyEntry: CustomFieldKeyClient = {
  allowedValues: [],
  multiValueEnabled: false,
  name: '',
};

export const CustomFieldsForm: FunctionComponent<CustomFieldsFormProps> = ({
  brandfolderKey,
  fetchCustomFieldKeys,
  hasRolloutMultiValueCustomFieldsFeature,
  rows,
  setRows,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [customFieldsToDelete, setCustomFieldsToDelete] = useState<string[]>([]);
  const ugtLocale = determineUGTLocale();

  const validName = (customFieldName: string): boolean => {
    const customFieldNames = rows.filter((key) => !!key.name);
    const duplicates = customFieldNames.filter(({ name }) => name.trim() === customFieldName?.trim()).length >= 2;
    return !!customFieldName && customFieldName.trim().length > 0 && !duplicates;
  };

  const validForm = (): boolean => {
    const customFieldNames = rows.map((key) => key.name);
    const emptyNames = customFieldNames.filter((name) => !!name === false).length > 0;
    const uniqueNames = Array.from(new Set(customFieldNames));
    const containsDuplicates = uniqueNames.length < customFieldNames.length;
    return !emptyNames && !containsDuplicates;
  };

  const updateRow = ({
    index,
    name,
    allowedValues,
    multiValueEnabled
  }: UpdateRowArguments): void => {
    setRows((prevState) => {
      const updatedState = [...prevState];
      if (index !== undefined) {
        updatedState[index] = {
          ...updatedState[index],
          ...allowedValues !== undefined && { allowedValues },
          ...multiValueEnabled !== undefined && { multiValueEnabled },
          ...name !== undefined && { name },
        };
      }
      return updatedState;
    });
  };

  const removeRow = (index: number): void => {
    const updatedCustomFieldKeys = [...rows];
    const fieldToDelete = updatedCustomFieldKeys.splice(index, 1)[0];
    setCustomFieldsToDelete((prevState) => (fieldToDelete.key ? [...prevState, fieldToDelete.key] : [...prevState]));

    const customFieldsForm = document.getElementById('custom-fields-form');
    if (customFieldsForm) {
      customFieldsForm.setAttribute('data-locked', 'true');
    }

    setRows(updatedCustomFieldKeys);
  };

  const handleSubmitCustomFieldKeys = (): Promise<ApiDatumResponse<CustomFieldKeyServer, 'custom_field_keys'>>[] => {
    const existingCustomFieldKeys = rows.filter((row) => !!(row.key));
    return existingCustomFieldKeys.map(({ allowedValues, key, multiValueEnabled, name }) => (
      updateCustomFieldKeys({
        attributes: { allowed_values: allowedValues, multi_value_enabled: multiValueEnabled, name: name?.trim() },
        customFieldKey: key || '',
        ugtLocale
      })
    ));
  };

  const handleSubmitNewCustomFieldKeys = (): Promise<CustomFieldKeysResponseObject[]> => {
    const newCustomFieldKeys = rows.filter((row) => (!row.key));
    const attributes = newCustomFieldKeys.map(({ allowedValues, multiValueEnabled, name }) => ({
      allowed_values: allowedValues, multi_value_enabled: multiValueEnabled, name: name?.trim()
    }));
    return submitNewCustomFields({
      attributes,
      resourceKey: brandfolderKey,
      resourceType: 'brandfolder',
      ugtLocale
    });
  };

  const resolveFormSubmission = (): void => {
    setCustomFieldsToDelete([]);
    setSubmitting(false);
    fetchCustomFieldKeys();
    Notify.create({
      title: bfTranslate("Controlled Custom Fields Updated"),
      type: "success"
    });
  };

  const submitForm = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault();
    const customFieldsForm = document.getElementById('custom-fields-form');
    if (customFieldsForm) {
      customFieldsForm.setAttribute('data-locked', 'false');
    }

    if (!validForm()) {
      Notify.create({
        title: bfTranslate("Please fix invalid fields before submitting"),
        type: "error"
      });
    } else if (!submitting) {
      setSubmitting(true);
      try {
        const deletePromises = customFieldsToDelete.map((customFieldKey) => (deleteCustomFieldKey({ customFieldKey })));
        await Promise.all(deletePromises);
        await Promise.all(handleSubmitCustomFieldKeys());
        await handleSubmitNewCustomFieldKeys();
        resolveFormSubmission();
      } catch (err) {
        setSubmitting(false);
        // Generally when people swap names for custom fields ex: A.name => B.name
        Notify.create({
          title: bfTranslate("An error occured and not all custom fields were updated."),
          body: bfTranslate("Please try again. Names may have been swapped and conflict with one another."),
          type: "error"
        });
      }
    }
  };

  return (
    <div className="container j-controlled-custom-fields">
      <Form id="custom-fields-form" onSubmit={submitForm}>
        <div className="form-headers">
          <h4 className="header-copy column-1">{bfTranslate('Key')}</h4>
          <h4 className="header-copy column-2">{bfTranslate('Values')} <span className="column-span">({bfTranslate('separate with commas')})</span></h4>
          {hasRolloutMultiValueCustomFieldsFeature && (
            <div className="column-3">
              <h4 className="header-copy">{bfTranslate('Allow multiple values')}</h4>
              <Tooltip
                tooltipContent={bfTranslate("If checked, asset keys can be assigned one or more of the values listed. If unchecked, asset keys can only be assigned one of the values listed.")}
                tooltipId="control-custom-fields-info"
              >
                <span className="bff-tool-tip" />
              </Tooltip>
            </div>
          )}
        </div>
        <div className="brandfolder-settings__control-custom-fields--list">
          {rows.map((displayedCustomFieldKey, index) => (
            <CustomFieldRow
              key={displayedCustomFieldKey.key || index}
              displayedCustomFieldKey={displayedCustomFieldKey}
              hasRolloutMultiValueCustomFieldsFeature={hasRolloutMultiValueCustomFieldsFeature}
              index={index}
              removeRow={removeRow}
              updateRow={updateRow}
              valid={validName(displayedCustomFieldKey.name)}
            />
          ))}
          {!rows.length && (
            <p className="empty-rows-copy">{bfTranslate('No custom fields to display. Click "Add new Custom Field" below to get sarted!')}</p>
          )}
        </div>
        <div className="add-custom-field-button">
          <SecondaryButton
            icon="bff-plus"
            onClick={(): void => setRows((prevState) => ([...prevState, blankCustomFieldKeyEntry]))}
          >
            {bfTranslate('Add new Custom Field')}
          </SecondaryButton>
        </div>
        <PrimaryButton
          className="custom-field-update-button j-modal-submit-button"
          isLoading={submitting}
          loadingCopy={bfTranslate('Updating Custom Fields')}
          size="large"
          type="submit"
        >
          {bfTranslate('Update Custom Fields')}
        </PrimaryButton>
      </Form>
    </div>
  );
};
